import { Logger } from '@footsteps/shared';
import { WozCaseEntity } from '../../woz-case/entities';
import type { WozCaseStorageAdapter } from './woz-case-storage.adapter';

const STORAGE_KEY = 'woz-case';
const VERSION_KEY = 'woz-case-version';

/**
 * localStorage adapter for persisting a single active WOZ case.
 * Only one case can be stored at a time.
 */
export class WozCaseStorageLocalAdapter implements WozCaseStorageAdapter {
  private readonly logger = new Logger(this.constructor.name);
  private readonly storageVersion = 2;

  async init(): Promise<void> {
    try {
      const stored = localStorage.getItem(VERSION_KEY);
      if (stored !== null && Number(stored) !== this.storageVersion) {
        await this.clear();
      }
      localStorage.setItem(VERSION_KEY, String(this.storageVersion));
    } catch (error) {
      this.logger.error('Failed to initialize storage version', error);
    }
  }

  async save(entity: WozCaseEntity): Promise<WozCaseEntity> {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(entity.toStorage()));
    } catch (error) {
      this.logger.error('Failed to save case to localStorage', error);
    }
    return entity;
  }

  async load(): Promise<WozCaseEntity | null> {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;

      return new WozCaseEntity(JSON.parse(raw));
    } catch (error) {
      this.logger.error('Failed to load case from localStorage', error);
      return null;
    }
  }

  async fetchOpen(): Promise<WozCaseEntity[]> {
    const entity = await this.load();
    return entity ? [entity] : [];
  }

  async remove(): Promise<void> {
    localStorage.removeItem(STORAGE_KEY);
  }

  async clear(): Promise<void> {
    localStorage.removeItem(STORAGE_KEY);
  }
}
